import { Camera, Group, Object3D, Scene, Vector3, Euler } from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";

import { IGameObject } from "../IGameObject";
import Loader from "../Loader";
import PlayerPhysics from "./PlayerPhysics";
import UserControls from "../Controls/UserControls";
import Barehand from "../Weapons/Barehand";
import { IWeapon } from "../Weapons/IWeapon";
import Sword from "../Weapons/Sword";
import Torch from "../Weapons/Torch";
import World from "../World";
import { PLAYER_HEIGHT, PLAYER_MODEL_SCALE } from "../Constants";

class CurrentPlayer implements IGameObject {
    public id: string;

    public type: "player" = "player";

    public position: Vector3;

    public physics: PlayerPhysics;

    public velocity: Vector3;

    public rotation: Euler;

    public controls: UserControls | null; // TODO controls should be aware of player, player should not be aware of controls

    public currentWeapon: number;

    public camera: Camera;

    public model: Object3D | null;

    public scene: Scene;

    public weapons: IWeapon[];

    public world: World;

    public team: "RED" | "BLUE" | null;

    constructor(
        camera: Camera,
        scene: Scene,
        loader: Loader,
        world: World,
        id: string,
        team: "RED" | "BLUE" | null
    ) {
        const playerStartPosition = new Vector3(0, 15, 0);
        const y =
            world.getGroundIntersection(
                new Vector3(
                    playerStartPosition.x,
                    playerStartPosition.y,
                    playerStartPosition.z
                ),
                15
            ) + PLAYER_HEIGHT;
        this.position = new Vector3(
            playerStartPosition.x,
            y,
            playerStartPosition.y
        );
        this.rotation = new Euler(0, 0, 0, "XZY");
        this.velocity = new Vector3();
        this.controls = null;
        this.model = null;
        this.physics = new PlayerPhysics(PLAYER_HEIGHT, 8, world, this);
        this.currentWeapon = 0;
        this.camera = camera;
        this.scene = scene;
        this.weapons = [
            new Barehand(),
            new Torch(loader, world),
            new Sword(loader),
        ];
        this.world = world;
        this.team = team;

        const mtlLoader = new MTLLoader();
        const objLoader = new OBJLoader();
        mtlLoader.load(
            `${process.env.ASSET_URL}/models/Knight_Male.mtl`,
            (materials) => {
                materials.preload();
                objLoader.setMaterials(materials);
                objLoader.load(
                    `${process.env.ASSET_URL}/models/Knight_Male.obj`,
                    (model: Group) => {
                        scene.add(model);
                        model.scale.multiplyScalar(PLAYER_MODEL_SCALE);
                        // eslint-disable-next-line no-param-reassign
                        model.visible = false;
                        this.model = model;
                        this.update(0);
                    }
                );
            }
        );
        this.id = id;
    }

    public getCurrentWeapon(): IWeapon {
        return this.weapons[this.currentWeapon];
    }

    public nextWeapon(): void {
        this.chooseWeapon(this.currentWeapon + 1);
    }

    public chooseWeapon(newWeapon: number): void {
        const { weapons } = this;
        const oldWeapon = this.getCurrentWeapon();
        this.currentWeapon = newWeapon % weapons.length;
        const newWeapon2 = this.getCurrentWeapon();
        this.updateWeaponGraphics(newWeapon2, oldWeapon);
    }

    public updateWeaponGraphics(newWeapon: IWeapon, oldWeapon: IWeapon): void {
        const { camera } = this;
        if (oldWeapon && oldWeapon.model !== null) {
            camera.remove(oldWeapon.model);
        }
        if (newWeapon.model) {
            camera.add(newWeapon.model);
        }
    }

    public update(delta: number): void {
        // Let the player control themself
        if (this.controls) this.controls.update(delta);

        // Update physics (momentum, gravity)
        this.physics.update(delta);

        // Switch weapon controls
        const currentWeapon = this.getCurrentWeapon();
        if (currentWeapon.update !== undefined) {
            currentWeapon.update();
        }
        // TODO should be a renderor pre-render function
        const { position, model, rotation } = this;
        if (model) {
            model.position.set(
                position.x,
                position.y - PLAYER_HEIGHT,
                position.z
            );
            model.rotation.set(rotation.x, rotation.y, rotation.z);
            model.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
        }
    }

    public setControls(controls: UserControls): void {
        this.controls = controls;
        this.controls.lock();
    }

    // eslint-disable-next-line class-methods-use-this
    public refresh(): void {
        // TODO current player doesn't need refreshing from server.. until anti cheat. Or should I put mutation code here?
    }
}

export default CurrentPlayer;
